// src/components/ActiveProvidersTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import ActiveProviderApi from "../APIs/activeProviderApi";
import PatientToProviderMessagesPanel from "./PatientToProviderMessagesPanel";

//export default function ActiveProvidersTab({ patientId, token }) {
export default function ActiveProvidersTab({ patientId }) {
  const [page, setPage] = useState(0);
  const [size] = useState(20);

  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsError, setCardsError] = useState(null);
  const [cardsPage, setCardsPage] = useState({
    content: [],
    totalPages: 0,
    number: 0,
  });

  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const cards = useMemo(() => cardsPage?.content ?? [], [cardsPage]);

  useEffect(() => {
    const ac = new AbortController();
    setCardsLoading(true);
    setCardsError(null);

    ActiveProviderApi.getActiveProviders(patientId, {
      page,
      size,
      signal: ac.signal,
    })
      .then((res) => setCardsPage(res))
      .catch((err) => {
        if (err.name !== "AbortError") setCardsError(err);
      })
      .finally(() => setCardsLoading(false));

    return () => ac.abort();
  }, [patientId, page, size]);

  const handleBackToList = () => {
    setSelectedProviderId(null);
  };

  // ---------- Render ----------
  return (
    <>
      {selectedProviderId ? (
        // Detail view — messaging only
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold">Active Provider</h3>
            <button
              onClick={handleBackToList}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
            >
              ← Back to Providers
            </button>
          </div>

          {/* Just the message panel for provider ↔ patient */}
          <PatientToProviderMessagesPanel
            patientId={patientId}
            providerId={selectedProviderId}
          />
        </div>
      ) : (
        // List view — provider cards
        <div>
          <h3 className="text-xl font-semibold mb-4">Active Providers</h3>

          {cardsLoading && (
            <div className="p-4 rounded border bg-white shadow-sm animate-pulse">
              Loading…
            </div>
          )}
          {cardsError && (
            <div className="p-4 rounded border bg-red-50 text-red-700">
              Error: {cardsError.message}
            </div>
          )}

          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            {cards.map((p) => (
              <button
                key={p.providerId}
                onClick={() => setSelectedProviderId(p.providerId)}
                className="w-full text-left p-4 rounded-xl border bg-white shadow-sm hover:shadow-md transition"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-base font-semibold">
                      {p.firstName} {p.lastName}
                    </div>
                    <div className="text-sm text-gray-600">{p.email}</div>
                    <div className="text-sm text-gray-500">
                      DOB: {p.dateOfBirth}
                    </div>
                    <div className="text-sm text-gray-500">
                      Phone: {p.phoneNumber || "—"}
                    </div>
                    <div className="text-sm text-gray-500">
                      Experience:{" "}
                      {p.yearsOfExperience != null
                        ? `${p.yearsOfExperience} years`
                        : "—"}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-xs uppercase text-gray-500">
                      Gender
                    </div>
                    <div className="text-sm font-medium">{p.gender || "—"}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Pagination */}
          {cardsPage.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-50"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {cardsPage.number + 1} of {cardsPage.totalPages}
              </span>
              <button
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm disabled:opacity-50"
                onClick={() =>
                  setPage((p) => Math.min(cardsPage.totalPages - 1, p + 1))
                }
                disabled={page >= cardsPage.totalPages - 1}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
